'use client';

import { FC, useEffect, useState } from 'react';
import { useClassName } from '../../../hooks/use-class-name';
import AppsShelveItem from './apps-shelve-item';
import { OwShelf } from '../../../interfaces/domain/ow-app-shelf.modal';
import { OwAppTile } from '../../../interfaces/domain/ow-app-tile.modal';
import useIsMobile from '../../../hooks/use-is-mobile';
import { useTranslation } from '../../../hooks/use-translation';
// -----------------------------------------------------------------------------
interface AppsShelveProps {
  appsData: OwShelf;
}
// -----------------------------------------------------------------------------
const AppsShelve: FC<AppsShelveProps> = props => {
  const { children, name, title, mobileListView, mobileLimit, sort } =
    props.appsData;

  const { t } = useTranslation();
  const sectionClassName = useClassName(
    {
      'list-type': mobileListView,
    },
    'apps-list-section'
  );
  const [limit, setLimit] = useState(
    mobileLimit ? mobileLimit : children?.length
  );
  const [visibleApps, setVisibleApps] = useState(children?.slice(0, limit));
  const [openIndex, setOpenIndex] = useState(null);
  const isMobile = useIsMobile();

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isMobile) {
      setVisibleApps(children);
    }
  }, []);

  // ---------------------------------------------------------------------------
  if (!children) {
    return null;
  }

  // ---------------------------------------------------------------------------
  const sortedTiles =
    sort?.field === 'default'
      ? visibleApps
      : visibleApps.sort((a, b) => {
        const titleA = a?.title?.toLowerCase() ?? '';
        const titleB = b?.title?.toLowerCase() ?? '';
        const direction = sort?.direction === 'asc' ? -1 : 1;
        if (titleA < titleB) {
          return 1 * direction;
        }

        if (titleA > titleB) {
          return -1 * direction;
        }

        return 0;
      });

  // ---------------------------------------------------------------------------
  const handleLoadMore = (): void => {
    const newLimit = limit + 10;
    setVisibleApps(children.slice(0, newLimit));
    setLimit(newLimit);
  };

  // ---------------------------------------------------------------------------
  const handleShowTileInfo = (index: number): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // ---------------------------------------------------------------------------

  return (
    <section className={sectionClassName}>
      <div className='apps-list-inner'>
        {title && <h2>{title}</h2>}

        <div className='apps-list-wrapper'>
          <ul className='apps-list'>
            {sortedTiles
              .filter((app: OwAppTile) => !app.disabled && !app.hidden)
              .map((tile, index) => {
                return (
                  <AppsShelveItem
                    item={tile as OwAppTile}
                    key={index}
                    isOpen={openIndex === index}
                    onClick={() => handleShowTileInfo(index)}
                    shelfName={name}
                  />
                );
              })}
          </ul>
        </div>

        {mobileListView && limit < children.length && (
          <div className='apps-list-actions'>
            <p className='desc'>
              {t('allAppsActions', {
                shown: limit.toString(),
                max: children.length.toString(),
              })}
            </p>
            <button
              className='btn-secondary-small show-more'
              onClick={handleLoadMore}
            >
              {t('loadMore')}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default AppsShelve;
